import { createTheme } from "@mui/material/styles";

import colors from "./base/colors";
import typography from "./base/typography";
import borders from "./base/borders";
import boxShadows from "./base/boxShadows";

import pxToRem from "./functions/pxToRem";
import rgba from "./functions/rgba";
import linearGradient from "./functions/linearGradient";
import boxShadow from "./functions/boxShadow";

const { primary, secondary, info, success, warning, error, light, dark, white, black, grey, text, background } =
  colors;

// Mobile-first breakpoints: design and build for the smallest screen first,
// then layer on enhancements as the viewport grows.
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },

  palette: {
    background: { default: background.default },
    text: { ...text, primary: text.main, secondary: grey[600] },
    transparent: colors.transparent,
    white,
    black,
    primary,
    secondary,
    info,
    success,
    warning,
    error,
    light,
    dark,
    grey,
    gradients: colors.gradients,
    socialMediaColors: colors.socialMediaColors,
    badgeColors: colors.badgeColors,
    inputBorderColor: colors.inputBorderColor,
    tabs: colors.tabs,
  },

  typography,

  borders,
  boxShadows,

  functions: {
    pxToRem,
    rgba,
    linearGradient,
    boxShadow,
  },

  shape: {
    borderRadius: 12,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: background.default,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borders.borderRadius.lg,
          textTransform: "none",
          fontWeight: typography.fontWeightMedium,
        },
      },
      variants: [
        // Stock MUI Button has no "gradient" variant — this reproduces the
        // one MKButtonRoot used to provide (the only variant/color
        // combination the app actually uses: variant="gradient" color="primary").
        {
          props: { variant: "gradient", color: "primary" },
          style: ({ theme: t }) => {
            const { palette, functions, boxShadows: shadows } = t;
            const { boxShadow, linearGradient } = functions;

            const background = linearGradient(palette.gradients.primary.main, palette.gradients.primary.state);

            const restShadow = shadows.colored.primary
              ? `${boxShadow([0, 3], [3, 0], palette.primary.main, 0.15)}, ${boxShadow(
                  [0, 3],
                  [1, -2],
                  palette.primary.main,
                  0.2
                )}, ${boxShadow([0, 1], [5, 0], palette.primary.main, 0.15)}`
              : "none";

            const hoverShadow = shadows.colored.primary
              ? `${boxShadow([0, 14], [26, -12], palette.primary.main, 0.4)}, ${boxShadow(
                  [0, 4],
                  [23, 0],
                  palette.primary.main,
                  0.15
                )}, ${boxShadow([0, 8], [10, -5], palette.primary.main, 0.2)}`
              : "none";

            return {
              background,
              color: palette.white.main,
              boxShadow: restShadow,
              "&:hover": {
                backgroundColor: palette.white.main,
                boxShadow: hoverShadow,
              },
              "&:focus:not(:hover)": {
                boxShadow: restShadow,
              },
              "&:disabled": {
                background,
                color: palette.white.main,
              },
            };
          },
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borders.borderRadius.lg,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: typography.fontWeightMedium,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: pxToRem(16),
          paddingRight: pxToRem(16),
        },
      },
    },
    // Replicates MKTypographyRoot's universal letter-spacing/case defaults
    // now that plain @mui/material Typography is used everywhere instead of
    // the MK wrapper — without this, the "overline" variant (used for the
    // mobile drawer's "Services" label) would fall back to MUI's built-in
    // uppercase-by-default styling for that variant.
    MuiTypography: {
      styleOverrides: {
        root: {
          letterSpacing: "-0.125px",
          textTransform: "none",
        },
      },
    },
  },
});

export default theme;
